import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "./components/layout/Layout";
import Index from "./pages/Index";
import CookieConsent from "./components/CookieConsent";
import PageTracker from "./components/PageTracker";

// Lazy load all pages except Index for code splitting
const Products = lazy(() => import("./pages/Products"));
const DarktraceProducts = lazy(() => import("./pages/DarktraceProducts"));
const StormshieldProducts = lazy(() => import("./pages/StormshieldProducts"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const About = lazy(() => import("./pages/About"));
const PartnerRegistration = lazy(() => import("./pages/PartnerRegistration"));
const News = lazy(() => import("./pages/News"));
const Contact = lazy(() => import("./pages/Contact"));
const Trainings = lazy(() => import("./pages/Trainings"));
const TrainingDetail = lazy(() => import("./pages/TrainingDetail"));
const FirewallBrands = lazy(() => import("./pages/FirewallBrands"));
const FirewallBrandPage = lazy(() => import("./pages/FirewallBrandPage"));
const FirewallProvincePage = lazy(() => import("./pages/FirewallProvincePage"));
const FirewallCityPage = lazy(() => import("./pages/FirewallCityPage"));
const FirewallNeighborhoodPage = lazy(() => import("./pages/FirewallNeighborhoodPage"));
const Solutions = lazy(() => import("./pages/Solutions"));
const SolutionPage = lazy(() => import("./pages/SolutionPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const CookiePreferences = lazy(() => import("./pages/CookiePreferences"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const CustomerRegistration = lazy(() => import("./pages/CustomerRegistration"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy load admin pages
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners"));
const AdminArticles = lazy(() => import("./pages/admin/AdminArticles"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminSeo = lazy(() => import("./pages/admin/AdminSeo"));
const AdminSeoPages = lazy(() => import("./pages/admin/AdminSeoPages"));
const AdminLocatieBeheer = lazy(() => import("./pages/admin/AdminLocatieBeheer"));
const AdminSitemap = lazy(() => import("./pages/admin/AdminSitemap"));
const AdminContent = lazy(() => import("./pages/admin/AdminContent"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminNavigation = lazy(() => import("./pages/admin/AdminNavigation"));
const AdminTrainings = lazy(() => import("./pages/admin/AdminTrainings"));
const AdminTrainingRegistrations = lazy(() => import("./pages/admin/AdminTrainingRegistrations"));
const AdminApiKeys = lazy(() => import("./pages/admin/AdminApiKeys"));
const AdminStatistics = lazy(() => import("./pages/admin/AdminStatistics"));
const AdminCookies = lazy(() => import("./pages/admin/AdminCookies"));
const AdminSeoAudit = lazy(() => import("./pages/admin/AdminSeoAudit"));
const AdminSeoCoverage = lazy(() => import("./pages/admin/AdminSeoCoverage"));
const AdminSeoIndex = lazy(() => import("./pages/admin/AdminSeoIndex"));
const AdminBrandGuidelines = lazy(() => import("./pages/admin/AdminBrandGuidelines"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminAuditLog = lazy(() => import("./pages/admin/AdminAuditLog"));
const AdminContentVersions = lazy(() => import("./pages/admin/AdminContentVersions"));
const AdminMFA = lazy(() => import("./pages/admin/AdminMFA"));
const AdminTasks = lazy(() => import("./pages/admin/AdminTasks"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminEmail = lazy(() => import("./pages/admin/AdminEmail"));
const AdminSocialMedia = lazy(() => import("./pages/admin/AdminSocialMedia"));
const AdminContentAssistent = lazy(() => import("./pages/admin/AdminContentAssistent"));
const AdminSitemapHosting = lazy(() => import("./pages/admin/AdminSitemapHosting"));
const AdminCustomerRegistrations = lazy(() => import("./pages/admin/AdminCustomerRegistrations"));
const AdminDatabase = lazy(() => import("./pages/admin/AdminDatabase"));
const AdminSitemapCheck = lazy(() => import("./pages/admin/AdminSitemapCheck"));
const AdminDeploy = lazy(() => import("./pages/admin/AdminDeploy"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminRoute = lazy(() => import("./components/admin/AdminRoute"));

import { AdminProvider } from "./contexts/AdminContext";

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes - Dutch */}
            <Route element={<Layout />}>
              <Route path="/" element={<Index />} />
              <Route path="/producten" element={<Products />} />
              <Route path="/producten/stormshield" element={<StormshieldProducts />} />
              <Route path="/producten/darktrace" element={<DarktraceProducts />} />
              <Route path="/producten/:slug" element={<ProductDetail />} />
              <Route path="/over-ons" element={<About />} />
              <Route path="/partner-worden" element={<PartnerRegistration />} />
              <Route path="/partner-registratie" element={<Navigate to="/partner-worden" replace />} />
              <Route path="/nieuws" element={<News />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/trainingen" element={<Trainings />} />
              <Route path="/trainingen/:slug" element={<TrainingDetail />} />
              <Route path="/firewalls" element={<FirewallBrands />} />
              <Route path="/firewalls/:brandSlug" element={<FirewallBrandPage />} />
              <Route path="/firewalls/:brandSlug/:provincieSlug" element={<FirewallProvincePage />} />
              <Route path="/firewalls/:brandSlug/:provincieSlug/:stadSlug" element={<FirewallCityPage />} />
              <Route path="/firewalls/:brandSlug/:provincieSlug/:stadSlug/:wijkSlug" element={<FirewallNeighborhoodPage />} />
              <Route path="/partners/:brandSlug" element={<FirewallBrandPage />} />
              <Route path="/oplossingen" element={<Solutions />} />
              <Route path="/oplossingen/:categorySlug" element={<SolutionPage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/voorwaarden" element={<Terms />} />
              <Route path="/algemene-voorwaarden" element={<Terms />} />
              <Route path="/cookie-voorkeuren" element={<CookiePreferences />} />
              <Route path="/cookiebeleid" element={<CookiePolicy />} />
              <Route path="/sitemap-overzicht" element={<Sitemap />} />
              
              {/* English routes */}
              <Route path="/products" element={<Products />} />
              <Route path="/products/stormshield" element={<StormshieldProducts />} />
              <Route path="/products/darktrace" element={<DarktraceProducts />} />
              <Route path="/products/:slug" element={<ProductDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/partner-registration" element={<PartnerRegistration />} />
              <Route path="/news" element={<News />} />
              <Route path="/trainings" element={<Trainings />} />
              <Route path="/trainings/:slug" element={<TrainingDetail />} />
              <Route path="/en/firewalls" element={<FirewallBrands />} />
              <Route path="/en/firewalls/:brandSlug" element={<FirewallBrandPage />} />
              <Route path="/en/firewalls/:brandSlug/:provincieSlug" element={<FirewallProvincePage />} />
              <Route path="/en/firewalls/:brandSlug/:provincieSlug/:stadSlug" element={<FirewallCityPage />} />
              <Route path="/en/firewalls/:brandSlug/:provincieSlug/:stadSlug/:wijkSlug" element={<FirewallNeighborhoodPage />} />
              <Route path="/en/partners/:brandSlug" element={<FirewallBrandPage />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/solutions/:categorySlug" element={<SolutionPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookie-preferences" element={<CookiePreferences />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              
              {/* French routes */}
              <Route path="/produits" element={<Products />} />
              <Route path="/produits/stormshield" element={<StormshieldProducts />} />
              <Route path="/produits/darktrace" element={<DarktraceProducts />} />
              <Route path="/produits/:slug" element={<ProductDetail />} />
              <Route path="/a-propos" element={<About />} />
              <Route path="/inscription-partenaire" element={<PartnerRegistration />} />
              <Route path="/actualites" element={<News />} />
              <Route path="/formations" element={<Trainings />} />
              <Route path="/formations/:slug" element={<TrainingDetail />} />
              <Route path="/pare-feux" element={<FirewallBrands />} />
              <Route path="/pare-feux/:brandSlug" element={<FirewallBrandPage />} />
              <Route path="/pare-feux/:brandSlug/:provincieSlug" element={<FirewallProvincePage />} />
              <Route path="/pare-feux/:brandSlug/:provincieSlug/:stadSlug" element={<FirewallCityPage />} />
              <Route path="/pare-feux/:brandSlug/:provincieSlug/:stadSlug/:wijkSlug" element={<FirewallNeighborhoodPage />} />
              <Route path="/solutions-fr" element={<Solutions />} />
              <Route path="/solutions-fr/:categorySlug" element={<SolutionPage />} />
              <Route path="/politique-confidentialite" element={<PrivacyPolicy />} />
              <Route path="/conditions-generales" element={<Terms />} />
            </Route>

            {/* Standalone pages (with own header/footer) */}
            <Route path="/klant-registratie" element={<CustomerRegistration />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin/*"
              element={
                <AdminProvider>
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                </AdminProvider>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="partners" element={<AdminPartners />} />
              <Route path="artikelen" element={<AdminArticles />} />
              <Route path="berichten" element={<AdminMessages />} />
              <Route path="instellingen" element={<AdminSettings />} />
              <Route path="seo" element={<AdminSeo />} />
              <Route path="seo-paginas" element={<AdminSeoPages />} />
              <Route path="locatiebeheer" element={<AdminLocatieBeheer />} />
              <Route path="seo-audit" element={<AdminSeoAudit />} />
              <Route path="seo-dekking" element={<AdminSeoCoverage />} />
              <Route path="seo-index" element={<AdminSeoIndex />} />
              <Route path="sitemap" element={<AdminSitemap />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="producten" element={<AdminProducts />} />
              <Route path="navigatie" element={<AdminNavigation />} />
              <Route path="trainingen" element={<AdminTrainings />} />
              <Route path="trainingen-aanmeldingen" element={<AdminTrainingRegistrations />} />
              <Route path="api-keys" element={<AdminApiKeys />} />
              <Route path="statistieken" element={<AdminStatistics />} />
              <Route path="cookies" element={<AdminCookies />} />
              <Route path="brand-guidelines" element={<AdminBrandGuidelines />} />
              <Route path="gebruikers" element={<AdminUsers />} />
              <Route path="audit-log" element={<AdminAuditLog />} />
              <Route path="versies" element={<AdminContentVersions />} />
              <Route path="beveiliging" element={<AdminMFA />} />
              <Route path="taken" element={<AdminTasks />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="email" element={<AdminEmail />} />
              <Route path="social-media" element={<AdminSocialMedia />} />
              <Route path="content-assistent" element={<AdminContentAssistent />} />
              <Route path="sitemap-hosting" element={<AdminSitemapHosting />} />
              <Route path="klantregistraties" element={<AdminCustomerRegistrations />} />
              <Route path="database" element={<AdminDatabase />} />
              <Route path="sitemap-check" element={<AdminSitemapCheck />} />
              <Route path="deploy" element={<AdminDeploy />} />
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <PageTracker />
        <CookieConsent />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
